import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Database, FileText, Sparkles, Upload, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useKnowledgeBases } from '@/hooks/useKnowledgeBases'
import { useCreateGenClairRun, useUploadGenClairFile } from '@/hooks/useGenClairRuns'
import { Link } from 'react-router-dom'

type SourceType = 'ragflow_dataset' | 'file_upload'

export function GenClairCreate() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data: kbData, isLoading: kbsLoading } = useKnowledgeBases({ page: 1, pageSize: 100, search: '' })
  const kbs = kbData?.items
  const createRun = useCreateGenClairRun()
  const uploadFile = useUploadGenClairFile()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [sourceType, setSourceType] = useState<SourceType>('ragflow_dataset')
  const [selectedKbId, setSelectedKbId] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [strategy, setStrategy] = useState('paragraph')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast({ title: 'Name required', description: 'Please enter a name for this run.', variant: 'destructive' })
      return
    }

    if (sourceType === 'ragflow_dataset' && !selectedKbId) {
      toast({ title: 'KB required', description: 'Select a knowledge base for RAGflow source.', variant: 'destructive' })
      return
    }

    if (sourceType === 'file_upload' && !file) {
      toast({ title: 'File required', description: 'Upload a text file for file upload source.', variant: 'destructive' })
      return
    }

    setIsSubmitting(true)
    try {
      const run = await createRun.mutateAsync({
        name: name.trim(),
        description: description.trim(),
        source_type: sourceType,
        knowledge_base_id: sourceType === 'ragflow_dataset' ? selectedKbId : undefined,
      })

      if (sourceType === 'file_upload' && file) {
        await uploadFile.mutateAsync({
          runId: run.id,
          file,
          strategy,
        })
      }

      toast({ title: 'Run created', description: 'GenClair pipeline has been started.' })
      navigate(`/app/genclair/${run.id}`)
    } catch (err: any) {
      const msg = err?.response?.data?.error || err.message || 'Failed to create run'
      toast({ title: 'Error', description: msg, variant: 'destructive' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <Button variant="ghost" size="sm" asChild className="mb-4">
        <Link to="/app/genclair">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to GenClair
        </Link>
      </Button>

      <div className="mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Sparkles className="h-8 w-8 text-purple-500" />
          New GenClair Run
        </h1>
        <p className="text-muted-foreground mt-2">
          Discover taxonomy, intents, and entity types from your data
        </p>
      </div>

      <div className="space-y-6">
        {/* Name & Description */}
        <Card>
          <CardHeader>
            <CardTitle>Run Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Q1 Support Tickets Analysis"
              />
            </div>
            <div>
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What data is this run analyzing?"
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Source Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Data Source</CardTitle>
            <CardDescription>Choose where GenClair will read data from</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSourceType('ragflow_dataset')}
                className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors ${
                  sourceType === 'ragflow_dataset'
                    ? 'border-primary bg-primary/5'
                    : 'border-muted hover:border-muted-foreground/30'
                }`}
              >
                <Database className="h-8 w-8 text-blue-500" />
                <span className="font-medium text-sm">Knowledge Base</span>
                <span className="text-xs text-muted-foreground text-center">
                  Read chunks from an existing KB
                </span>
              </button>
              <button
                type="button"
                onClick={() => setSourceType('file_upload')}
                className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors ${
                  sourceType === 'file_upload'
                    ? 'border-primary bg-primary/5'
                    : 'border-muted hover:border-muted-foreground/30'
                }`}
              >
                <FileText className="h-8 w-8 text-amber-500" />
                <span className="font-medium text-sm">File Upload</span>
                <span className="text-xs text-muted-foreground text-center">
                  Upload a TXT or text file
                </span>
              </button>
            </div>

            {/* KB selector */}
            {sourceType === 'ragflow_dataset' && (
              <div>
                <Label>Knowledge Base</Label>
                {kbsLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mt-1">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                  </div>
                ) : (
                  <Select value={selectedKbId} onValueChange={setSelectedKbId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a knowledge base" />
                    </SelectTrigger>
                    <SelectContent>
                      {(kbs || []).map((kb: any) => (
                        <SelectItem key={kb.id} value={kb.id}>
                          {kb.name} ({kb.ready_document_count} docs ready)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* File upload */}
            {sourceType === 'file_upload' && (
              <>
                <div>
                  <Label>File</Label>
                  <div className="mt-1">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:border-muted-foreground/30 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          {file ? file.name : 'Click to upload a text file'}
                        </p>
                        {file && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {(file.size / 1024).toFixed(1)} KB
                          </p>
                        )}
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept=".txt,.text,.csv,.md"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                      />
                    </label>
                  </div>
                </div>
                <div>
                  <Label>Chunking Strategy</Label>
                  <Select value={strategy} onValueChange={setStrategy}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="line_by_line">Line-by-line</SelectItem>
                      <SelectItem value="fixed_size">Fixed-size chunks</SelectItem>
                      <SelectItem value="paragraph">Paragraph-based</SelectItem>
                      <SelectItem value="section">Section-based</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {strategy === 'line_by_line' && 'Each line becomes a chunk — for pre-chunked data'}
                    {strategy === 'fixed_size' && 'Uniform character-length chunks — for unstructured text'}
                    {strategy === 'paragraph' && 'Split on blank lines — for narrative text'}
                    {strategy === 'section' && 'Detect headers and group content — for structured docs'}
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" asChild>
            <Link to="/app/genclair">Cancel</Link>
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" />Start Run</>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
